import { useState, useEffect, useRef } from "react";
import { Users, Building2, ChevronDown, Check, X, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import graphqlRequest from "../../requestMethod";
import "./pocFacilities.scss";

const PocFacilities = ({ pocFacilities, onDataChange }) => {
  const [selectedPoc, setSelectedPoc] = useState("");
  const [selectPocWithNumber, setSelectPocWithNumber] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFacilities, setSelectedFacilities] = useState({
    foodMeals: false,
    transportation: false,
    accommodation: false,
    showOthers: false,
    others: "",
  });
  const [fetchContactDetails, setFetchContactDetails] = useState([]);

  const [showAddPocForm, setShowAddPocForm] = useState(false);
  const [newPoc, setNewPoc] = useState({ name: "", email: "", phone: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const pocOptions = [
    "Dr. Sarah Johnson - Chief Medical Officer",
    "Nurse Manager Lisa Chen - ICU Supervisor",
    "Dr. Michael Rodriguez - Emergency Department Head",
    "Rebecca Thompson - HR Director",
    "Dr. James Wilson - Cardiology Department Head",
    "Maria Garcia - Nursing Supervisor",
    "Dr. Emily Davis - Surgery Department Coordinator",
    "John Anderson - Operations Manager",
    "Dr. Amanda White - Pediatrics Department Head",
    "Pratika - Head Nurse",
  ];

  const facilityOptions = [
    { key: "foodMeals", label: "Food & Meals" },
    { key: "transportation", label: "Transportation" },
    { key: "accommodation", label: "Accommodation" },
    { key: "showOthers", label: "Others" },
  ];

  let loginData = localStorage.getItem("auth");
  let token = JSON.parse(loginData)?.accessToken;

  function fetchContactDetailsUser() {
    const query = `
         query{
           withAuth(token:"${token}"){
             listOrganizationUsers {
               id
               email
               contact
               name 
             }
            }
          }
       `;
    graphqlRequest(query).then((response) => {
      // console.log("response", response.data.withAuth.listOrganizationUsers);
      setFetchContactDetails(response.data.withAuth.listOrganizationUsers);
    });
  }

  useEffect(() => {
    fetchContactDetailsUser();
  }, []);

  const queryClient = useQueryClient();

  //  const { data: shifts = [], isLoading } = {}
  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ["/api/shifts"],
    queryFn: async () => {
      const response = await fetch("/api/shifts");
      if (!response.ok) throw new Error("Failed to fetch shifts");
      return response.json();
    },
  });

  const handlePocSelect = (poc) => {
    let pocDataWithNumber = `${poc.name} (${poc.email}) ${poc.contact}`;
    let pocData = `${poc.name} (${poc.email})`;
    setSelectedPoc(pocData);
    setSelectPocWithNumber(pocDataWithNumber);
    setIsDropdownOpen(false);
    onDataChange?.({
      pocDetails: pocDataWithNumber,
      isFood: selectedFacilities.isFood,
      isTransport: selectedFacilities.isTransport,
      others: selectedFacilities.others || "",
    });

    // ✅ Update all shift entries in the cache
    const currentShifts = queryClient.getQueryData(["/api/shifts"]) || [];

    const updatedShifts = currentShifts.map((shift) => ({
      ...shift,
      pocDetails: pocDataWithNumber,
      isFood: selectedFacilities.isFood,
      isTransport: selectedFacilities.isTransport,
      others: selectedFacilities.others || "",
    }));

    queryClient.setQueryData(["/api/shifts"], updatedShifts);
  };

  const handleFacilityToggle = (key) => {
    // console.log("key-facilities", key);
    const updated = { ...selectedFacilities, [key]: !selectedFacilities[key] };
    // console.log("updated", updated);
    setSelectedFacilities(updated);
    onDataChange?.({
      pocDetails: selectPocWithNumber,
      isFood: updated.foodMeals,
      isTransport: updated.transportation,
      isAccommodation: updated.accommodation,
      others: updated.others,
    });

    const currentShifts = queryClient.getQueryData(["/api/shifts"]) || [];

    const updatedShifts = currentShifts.map((shift) => ({
      ...shift,
      //   pocDetails: updated.pocDetails,
      isFood: updated.foodMeals,
      isTransport: updated.transportation,
      isAccommodation: updated.accommodation,
      others: updated.others,
    }));

    queryClient.setQueryData(["/api/shifts"], updatedShifts);
  };

  const handleOthersInputChange = (e) => {
    const updated = {
      ...selectedFacilities,
      others: e.target.value,
    };
    setSelectedFacilities(updated);

    onDataChange?.({
      pocDetails: selectPocWithNumber,
      isFood: updated.foodMeals,
      isTransport: updated.transportation,
      isAccommodation: updated.accommodation,
      others: updated.others,
    });

    const currentShifts = queryClient.getQueryData(["/api/shifts"]) || [];

    const updatedShifts = currentShifts.map((shift) => ({
      ...shift,
      others: updated.others,
    }));

    queryClient.setQueryData(["/api/shifts"], updatedShifts);
  };

  useEffect(() => {
    if (pocFacilities) {
      setSelectedFacilities((prev) => ({
        pocDetails: pocFacilities?.pocDetails || "",
        foodMeals: pocFacilities.isFood || false,
        transportation: pocFacilities.isTransport || false,
        accommodation: pocFacilities.isAccommodation || false,
        showOthers:
          typeof prev.showOthers === "boolean"
            ? prev.showOthers
            : !!pocFacilities.others,
        others: pocFacilities.others || "",
      }));

      if (!selectedPoc && pocFacilities.pocDetails) {
        setSelectedPoc(pocFacilities.pocDetails);
        setSelectPocWithNumber(pocFacilities.pocDetails);
      }
    }
  }, [pocFacilities]);

  const handlePocCreate = async () => {
    const { name, email, phone } = newPoc;
    if (!name || !email || !phone) {
      setErrorMsg("All fields are required.");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");

    const query = `
      mutation {
        mutationWithAuth(token: "${token}") {
          fabricate {
            createPOC(
              name: "${name}"
              email: "${email}"
              phone: "${phone}"
            )
          }
        }
      }
    `;

    try {
      const response = await graphqlRequest(query);
      if (response?.data?.mutationWithAuth?.fabricate?.createPOC) {
        const newPocEntry = { name, email, contact: phone };
        setFetchContactDetails((prev) => [...prev, newPocEntry]);
        handlePocSelect(newPocEntry);
        setNewPoc({ name: "", email: "", phone: "" });
        setShowAddPocForm(false);
      } else {
        setErrorMsg(response?.errors?.[0]?.message || "Error creating POC.");
      }
    } catch (err) {
      setErrorMsg("Failed to create POC.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const dropdownRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false); // 👈 Close dropdown
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (showAddPocForm) {
      document.body.style.overflow = "hidden"; // 🔐 Freeze background scroll
    } else {
      document.body.style.overflow = "auto"; // 🔓 Restore scroll
    }

    return () => {
      document.body.style.overflow = "auto"; // 🔁 Cleanup on unmount
    };
  }, [showAddPocForm]);

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setNewPoc({ ...newPoc, email });

    // Simple email pattern
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      setEmailError("Invalid email format");
    } else {
      setEmailError("");
    }
  };

  // console.log("pocFacilities", pocFacilities);
  //   console.log("fetchContactDetails", fetchContactDetails);
  return (
    <div className="poc-container">
      {/* Header */}
      <div className="poc-header">
        <Users className="icon" />
        <h2>POC (Shift In-Charge) & On-Site Facilities </h2>
      </div>

      {/* Shift Incharge */}
      <div className="poc-section">
        {/* <div className="poc-section-title">
          <Users className="icon" />
          <h3>Shift Incharge (POC)</h3>
        </div> */}

        <div className="poc-section-title">
          <div className="left-section">
            <Users className="icon" />
            <h3> POC (Shift In-Charge) </h3>
          </div>

          {/* {!showAddPocForm && (
            <button
              className="add-poc-button"
              onClick={() => setShowAddPocForm(true)}
            >
              <Plus className="plus-icon" />
              Add New POC
            </button>
          )} */}
        </div>

        {/* <label>Select Shift Incharge</label> */}
        <div
          className="dropdown"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <span>
            {selectedPoc ||
              pocFacilities?.pocDetails ||
              "Select POC (Shift In-Charge)   "}
          </span>
          <span className="dropdown-arrow">
            <ChevronDown size={18} />
          </span>
        </div>
        {isDropdownOpen && (
          <div className="dropdown-list" ref={dropdownRef}>
            {/* Existing POC List */}
            {fetchContactDetails.map((poc, i) => (
              <div
                key={i}
                className="dropdown-item"
                onClick={() => handlePocSelect(poc)}
              >
                {poc.name} ({poc.email})
              </div>
            ))}

            {/* Separator */}
            <div className="dropdown-separator" />

            {/* Add New POC Button */}
            <div
              className="dropdown-item add-new-poc"
              onClick={() => {
                setIsDropdownOpen(false);
                setShowAddPocForm(true);
              }}
            >
              <Plus
                size={16}
                className="plus-icon"
                style={{ marginBottom: "3px" }}
              />
              Add New POC (Shift In-Charge)
            </div>
          </div>
        )}
      </div>

      {/* Add POC Modal */}
      {showAddPocForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="close-button"
              onClick={() => setShowAddPocForm(false)}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h3 className="modal-title">Add New POC (Shift In-Charge)</h3>

            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={newPoc.name}
              // onChange={(e) => setNewPoc({ ...newPoc, name: e.target.value })}
              onChange={(e) => {
                const alphabetOnly = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                setNewPoc({ ...newPoc, name: alphabetOnly });
              }}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={newPoc.email}
              onChange={handleEmailChange}
              // onChange={(e) => setNewPoc({ ...newPoc, email: e.target.value })}
            />
            {emailError && (
              <p style={{ color: "red", fontSize: "0.85rem" }}>{emailError}</p>
            )}
            <input
              type="text"
              name="phone"
              placeholder="Contact Number"
              value={newPoc.phone}
              // onChange={(e) => setNewPoc({ ...newPoc, phone: e.target.value })}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d{0,10}$/.test(val)) {
                  setNewPoc({ ...newPoc, phone: val });
                }
              }}
            />

            {errorMsg && <p className="error-msg">{errorMsg}</p>}

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setShowAddPocForm(false)}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handlePocCreate}
                disabled={isSubmitting || emailError}
              >
                {isSubmitting ? "Saving..." : "Save POC"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* On-Site Facilities */}
      <div className="poc-section">
        <div className="poc-section-title-1">
          <Building2 className="icon" />
          <h3>On-Site Facilities Provided</h3>
        </div>
        <div className="facilities-list">
          {facilityOptions.map((facility) => (
            <div
              key={facility.key}
              className={`facility-card ${
                selectedFacilities[facility.key] ? "active" : ""
              }`}
              onClick={() => handleFacilityToggle(facility.key)}
            >
              <div className="checkbox">
                {selectedFacilities[facility.key] && <Check size={16} />}
              </div>
              <span>{facility.label}</span>
            </div>
          ))}
        </div>

        {/* Conditional textarea if 'Others' is selected */}
        {selectedFacilities?.showOthers && (
          <div className="other-facilities">
            <label>Specify Other Facilities</label>
            <textarea
              placeholder="Please specify other facilities provided"
              className="other-facilities__textarea"
              value={selectedFacilities.others}
              onChange={handleOthersInputChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PocFacilities;
